/** W3 broadcast-native stats_panel (PMA-BCAST-013). Panel + animated numbers apply directly. */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../../logger/types";
import { AnimatedNumber } from "../animation/AnimatedNumber";
import { computeNativeStats, type NativeStatRow } from "./nativeStats";

const Badge = ({
  kind,
  side,
  n,
}: {
  kind: "yellow" | "red";
  side: string;
  n: number;
}) => (
  <span
    data-testid={`stat-fouls-${kind}-${side}`}
    style={{
      display: "inline-flex",
      minWidth: "1.3em",
      height: "1.3em",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "0.15em",
      fontSize: "0.85em",
      fontWeight: 700,
      // Chips sit on their own solid fill: no inherited panel outline/halo.
      WebkitTextStroke: 0,
      paintOrder: "normal",
      textShadow: "none",
      background: kind === "yellow" ? "#facc15" : "#dc2626",
      color: kind === "yellow" ? "#0f172a" : "#fff",
    }}
  >
    {n}
  </span>
);

function Value({ row, side }: { row: NativeStatRow; side: "home" | "away" }) {
  const v = row[side];
  const text = side === "home" ? row.homeText : row.awayText;
  if (v === null) return <span>—</span>;
  if (text !== undefined) {
    const pct = text.slice(text.indexOf(" - ") + 3);
    return (
      <span>
        <AnimatedNumber value={v} /> - {pct}
      </span>
    );
  }
  return (
    <span>
      <AnimatedNumber value={v} />
      {row.suffix ?? ""}
    </span>
  );
}

export function NativeStatsPanel({
  match,
  events,
  options,
  xg,
}: {
  match: Match;
  events: MatchEvent[];
  options: Record<string, unknown>;
  xg?: { home: number; away: number } | null;
}) {
  const { t } = useTranslation("logger");
  const tl = t as unknown as (k: string, fb?: string) => string;
  const wanted = Array.isArray(options.rows)
    ? (options.rows as unknown[]).filter(
        (r): r is string => typeof r === "string",
      )
    : [];
  const rows = useMemo(
    () => computeNativeStats(match, events, xg),
    [match, events, xg],
  );
  const shown = wanted.length
    ? rows.filter((r) => wanted.includes(r.key))
    : rows;
  return (
    <div
      data-testid="broadcast-native-stats"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        color: "#f8fafc",
        boxSizing: "border-box",
        padding: "0.5em 0.9em",
      }}
    >
      {shown.map((r) => (
        <div
          key={r.key}
          data-testid={`stat-${r.key}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: "0.6em",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              display: "flex",
              gap: "0.4em",
              alignItems: "center",
            }}
          >
            <div data-testid="stat-home-value">
              <Value row={r} side="home" />
            </div>
            {r.cards && (
              <>
                <Badge kind="yellow" side="home" n={r.cards.home.yellow} />
                <Badge kind="red" side="home" n={r.cards.home.red} />
              </>
            )}
          </div>
          <div
            style={{
              textAlign: "center",
              fontSize: "0.8em",
              opacity: "var(--bcast-muted-opacity, 0.85)",
              color: "var(--bcast-muted-color, inherit)",
              fontWeight: "var(--bcast-muted-weight, inherit)" as never,
            }}
          >
            {tl(r.labelKey, r.labelFallback)}
          </div>
          <div
            style={{
              fontWeight: 600,
              display: "flex",
              gap: "0.4em",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {r.cards && (
              <>
                <Badge kind="yellow" side="away" n={r.cards.away.yellow} />
                <Badge kind="red" side="away" n={r.cards.away.red} />
              </>
            )}
            <div data-testid="stat-away-value">
              <Value row={r} side="away" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
